import React from "react";
import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/i18n";
import { Reveal, MaskLines } from "./Reveal";

export const Contact = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const hours = COMPANY.hours[lang];

  const cards = [
    { icon: Mail, label: c.write, value: COMPANY.email, href: `mailto:${COMPANY.email}`, testid: "contact-email" },
    { icon: MapPin, label: c.visit, value: COMPANY.address, href: COMPANY.mapsUrl, testid: "contact-address" },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 bg-[#0A0A0B] border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#D4AF37] mb-6">
            {c.overline}
          </p>
        </Reveal>
        <h2 className="font-display font-medium tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white max-w-2xl">
          <MaskLines lines={[c.title]} />
        </h2>

        <div className="mt-14 grid lg:grid-cols-2 gap-10">
          <div className="grid sm:grid-cols-2 gap-4 auto-rows-min">
            {/* Phone (two numbers) */}
            <Reveal>
              <div data-testid="contact-phone" className="block h-full rounded-2xl border border-white/10 bg-[#0F1012] p-6 hover:border-[#D4AF37]/50 hover-lift">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] mb-4">
                  <Phone size={20} />
                </span>
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">{c.call}</p>
                <a href={`tel:${COMPANY.phoneHref}`} className="mt-1.5 block text-white font-medium hover:text-[#D4AF37] transition-colors">
                  {COMPANY.phone}
                </a>
                <a href={`tel:${COMPANY.phone2Href}`} className="mt-1 block text-white font-medium hover:text-[#D4AF37] transition-colors">
                  {COMPANY.phone2}
                </a>
              </div>
            </Reveal>
            {cards.map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={i} delay={(i + 1) * 0.06}>
                  <a
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={item.testid}
                    className="block h-full rounded-2xl border border-white/10 bg-[#0F1012] p-6 hover:border-[#D4AF37]/50 hover-lift"
                  >
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] mb-4">
                      <Icon size={20} />
                    </span>
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">{item.label}</p>
                    <p className="mt-1.5 text-white font-medium">{item.value}</p>
                  </a>
                </Reveal>
              );
            })}

            {/* Working hours */}
            <Reveal delay={0.2} className="sm:col-span-2">
              <div data-testid="contact-hours" className="rounded-2xl border border-white/10 bg-[#0F1012] p-6 hover:border-[#D4AF37]/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#D4AF37]/10 text-[#D4AF37]">
                    <Clock size={20} />
                  </span>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">{c.hours}</p>
                </div>
                <ul className="divide-y divide-white/5">
                  {hours.map((row, i) => (
                    <li key={i} className="flex items-center justify-between py-2.5">
                      <span className="text-zinc-300 text-sm">{row.d}</span>
                      <span className={`text-sm font-medium ${/ne radimo|closed/i.test(row.h) ? "text-zinc-600" : "text-white"}`}>
                        {row.h}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="relative rounded-2xl overflow-hidden border border-white/10 h-full min-h-[380px] group">
              <iframe
                title="Kruška Distribucija — lokacija"
                data-testid="contact-map"
                src={COMPANY.mapEmbed}
                className="w-full h-full min-h-[380px] grayscale contrast-125 opacity-90 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href={COMPANY.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions"
                className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D4AF37] text-black text-sm font-bold hover:bg-[#F3CE5A] active:scale-95 transition-all shadow-lg"
              >
                <Navigation size={16} /> {c.directions}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
